import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { Technique } from '../types';

function mapDoc(doc: { id: string; data(): Record<string, unknown> }): Technique {
  const d = doc.data();
  return {
    id: doc.id,
    name: d.name as string,
    japaneseName: (d.japaneseName as string) || undefined,
    categoryId: d.categoryId as string,
    tier: d.tier as Technique['tier'],
    type: d.type as Technique['type'],
    uniform: (d.uniform as Technique['uniform']) || undefined,
    arts: Array.isArray(d.arts) && d.arts.length > 0 ? (d.arts as Technique['arts']) : undefined,
    instagramUrl: (d.videoUrl as string) || undefined,
    author: (d.author as string) || undefined,
    attribution: (d.attribution as string) || undefined,
    context: (d.context as string) || undefined,
    rule: (d.rule as string) || undefined,
  };
}

/**
 * Firestore subscriptions to the admin-only queue and archive collections.
 * Returns empty arrays when the caller is not admin so the library can render
 * a unified list unconditionally.
 */
export function useAdminQueue(isAdmin: boolean) {
  const [pending, setPending] = useState<Technique[]>([]);
  const [archived, setArchived] = useState<Technique[]>([]);

  // NOTE: a listener that errors is cancelled permanently and the list
  // silently freezes at its last value — log it so a stale queue is diagnosable.
  useEffect(() => {
    if (!isAdmin) { setPending([]); return; }
    const q = query(collection(db, 'audit_techniques'), where('status', '==', 'pending'));
    return onSnapshot(q, (snap) => setPending(snap.docs.map(mapDoc)),
      (err) => console.error('Admin pending-queue listener died:', err));
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) { setArchived([]); return; }
    return onSnapshot(collection(db, 'archived_techniques'), (snap) => setArchived(snap.docs.map(mapDoc)),
      (err) => console.error('Admin archive listener died:', err));
  }, [isAdmin]);

  return { pending, archived };
}
